import analytics from './analytics';

// Onboarding milestones forwarded to GTM/GA4 so Google Ads can optimize for
// users who actually activate, instead of for signup-page visits.
//
// The code decides WHEN a milestone happened; GTM decides WHERE it goes. Each
// name below is pushed to the dataLayer as a custom event, and GTM fans it out
// to GA4, Google Ads, and any pixel added later (Meta, Reddit, TikTok) with no
// code change. What GTM must not do is infer the milestone itself from URLs or
// clicks: that is how the duplicated Created_Widget/created_widget events and
// the page-view-based Signup conversion happened.
//
// Each milestone fires at most once. Three layers enforce that, weakest first:
// this localStorage guard, the server only reporting the deep milestones on
// their first occurrence, and the Google Ads conversion action's "One" per
// click counting, which is the authoritative one.
export const MILESTONES = {
  TESTIMONIALS_IMPORTED: 'testimonials_imported',
  WIDGET_CREATED: 'widget_created',
  WIDGET_INSTALLED: 'widget_installed',
  FORM_CREATED: 'form_created',
  FORM_SUBMITTED: 'form_submitted',
  SURVEY_CREATED: 'survey_created',
  // A respondent finished a survey, so it is live in front of the customer's own customers.
  SURVEY_ANSWERED: 'survey_answered',
};

// Fires the first time an account does anything meaningful in the product.
// Import this OR the individual milestones into Google Ads, never both.
const ACTIVATION_EVENT = 'activated';

const STORAGE_PREFIX = 'spo_conv_';
// Remembers which address was published, so SWR revalidation does not re-push the same one and a
// different account signing in without a reload replaces it rather than inheriting the previous
// user's hash.
let publishedEmail = null;

/**
 * Publishes the signed-in user's hashed email for Google Ads enhanced conversions.
 *
 * These milestones land days after the ad click, and Safari caps the click cookie at 7 days, so a
 * widget install is routinely invisible to cookie-based attribution. Matching on a hashed email
 * recovers those conversions.
 *
 * The email is SHA-256'd here rather than pushed raw: GTM would hash it anyway, and hashing first
 * keeps the plaintext address out of the dataLayer where any script on the page could read it.
 */
export async function setUserData(email) {
  if(typeof window === 'undefined' || !email) {
    return;
  }
  const normalized = String(email).trim().toLowerCase();
  if(publishedEmail === normalized) {
    return;
  }
  // crypto.subtle only exists in a secure context, so this is a no-op on plain http in local dev.
  if(!window.crypto || !window.crypto.subtle) {
    return;
  }
  // Await callers before firing a conversion: the tag reads user_data off the dataLayer when it
  // fires, so pushing after the conversion would leave it with nothing to match on.
  try {
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalized));
    const hashed = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ user_data: { sha256_email_address: hashed } });
    // Only after a successful push, so a transient failure can still be retried.
    publishedEmail = normalized;
  } catch(err) {
    // Identity is an optimisation for attribution; never let it break the page.
  }
}

export default function trackConversionEvent(milestone, properties = {}) {
  if(!milestone) {
    return;
  }
  if(fireOnce(milestone, properties)) {
    // Any first milestone also counts as activation, so a single Ads
    // conversion action covers "imported testimonials OR created a widget".
    fireOnce(ACTIVATION_EVENT, { milestone, ...properties });
  }
}

// Helpers

function fireOnce(eventName, properties) {
  if(typeof window === 'undefined') {
    return false;
  }
  const key = `${STORAGE_PREFIX}${eventName}`;
  let eventId;
  // localStorage throws in private mode / when storage is full, and a tracking
  // event must never break the action the user just performed.
  try {
    if(window.localStorage.getItem(key)) {
      return false;
    }
    eventId = newEventId();
    // The stored value doubles as the event id, so the id stays stable for this
    // milestone. Pixels that later pair a browser event with a server-side one
    // (Meta CAPI, Reddit CAPI) deduplicate on exactly this.
    window.localStorage.setItem(key, eventId);
  } catch(err) {
    return false;
  }
  analytics.track(eventName, { eventId, ...properties });
  return true;
}

function newEventId() {
  if(window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
